import { useEffect, useRef } from "react";

import { GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useRouteState } from "@/hooks/useRouteState";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";
import { DoOptimizePages } from "@coreapp/utils/DoOptimizePages";

/**
 * Syncs ?zoom= URL param into coreapp's wsMgr.Zoom. Direction is URL →
 * coreapp; the zoom menu writes URL via setRouteParam("zoom", value),
 * this hook drives coreapp from the URL.
 *
 * De-dupes via a (doc, value) ref so steady-state re-renders don't
 * re-trigger Zoom. Re-runs on chartDocument update so cold-start
 * ?zoom=N applies once the canvas panel is ready.
 */
export const useZoomSync = () => {
  const { zoom: zoomParam } = useRouteState();
  const { chartDocument } = useChartDocumentContext() ?? {};
  const lastAppliedRef = useRef<{ doc: unknown; value: string } | null>(null);

  useEffect(() => {
    if (zoomParam == null) return;
    const wsMgr = GetWSMgr();
    if (!wsMgr?.fCanvasPanel) return;
    const doc = wsMgr.GetDoc();
    if (!doc) return;

    const prev = lastAppliedRef.current;
    if (prev && prev.doc === doc && prev.value === zoomParam) return;

    const numeric = parseInt(zoomParam, 10);
    if (!Number.isFinite(numeric)) return;
    const currentPercent = Math.floor(wsMgr.fCanvasPanel.GetScaleFactor() * 100);
    if (currentPercent === numeric) {
      lastAppliedRef.current = { doc, value: zoomParam };
      return;
    }
    lastAppliedRef.current = { doc, value: zoomParam };
    wsMgr.Zoom(numeric, false);
    DoOptimizePages(wsMgr);
    wsMgr.fCanvasPanel.PanToTopCenter?.();
    ReRenderCanvas();
    wsMgr.BroadcastTopic?.("BoundaryChange", undefined);
  }, [zoomParam, chartDocument]);
};
